// Клиент админских эндпоинтов ИИ-функций (модели, настройки, ключ).
// На момент написания эндпоинты ещё не существуют — сервер пишет параллельный
// агент, поэтому типы и пути строго из SPEC.md, без оглядки на бэкенд.
import { request } from '../auth/request';
import type { ApiResult } from '../auth/request';

/**
 * Всё, у чего в настройках своя модель. Первые три — источники распознавания,
 * `magic` — подбор вариантов поездки из библиотеки, `prices` — поиск
 * актуальной цены рейса по сайтам бронирования: задачи разные, и модель каждой
 * нужна своя (см. admin.ai.source.*.hint, server/ai/magic.mjs и
 * server/prices/). Набор обязан совпадать с MODEL_SOURCES на сервере
 * (server/ai/settings.mjs) — по нему проверяются и PUT настроек, и
 * POST /api/admin/ai/test.
 */
export type AiSource = 'text' | 'url' | 'screenshot' | 'magic' | 'prices';

/** Числовые настройки, которые можно задать и общими, и на конкретную задачу. */
export interface AiTuning {
  maxTokens: number;
  temperature: number;
  timeoutSec: number;
  /** 0 = без лимита. */
  dailyLimit: number;
}

export type AiTuningField = keyof AiTuning;

/**
 * Переопределения на задачу. Поля необязательные: отсутствующее наследуется от
 * общей настройки. `null` в PUT стирает переопределение (вернуть наследование
 * иначе было бы нечем).
 */
export type AiOverrides = Partial<Record<AiSource, Partial<AiTuning>>>;

export interface AiSettings extends AiTuning {
  enabled: boolean;
  /** id модели на источник; '' — модель не выбрана. */
  models: Record<AiSource, string>;
  /**
   * Сколько скриншотов пользователь отправляет за один запрос. Общая настройка,
   * а не переопределение задачи: пачка бывает только у скриншотов. Этот же
   * предел уезжает клиенту в `/api/recognize/config`.
   */
  maxImages: number;
  overrides: AiOverrides;
}

/** То же, но для отправки: null означает «стереть переопределение». */
export type AiOverridesPatch = Partial<Record<AiSource, Partial<Record<AiTuningField, number | null>>>>;

export interface AiKeyInfo {
  present: boolean;
  /** Откуда взят ключ: из БД или из переменной окружения; null — ключа нет. */
  source: 'db' | 'env' | null;
  /** Последние 4 символа ключа для опознания, либо ''. */
  tail: string;
}

export interface AiModel {
  id: string;
  name: string;
  contextLength: number | null;
  /** $ за 1M токенов. */
  promptPrice: number | null;
  completionPrice: number | null;
  vision: boolean;
  /** Обе цены равны 0. */
  free: boolean;
  /**
   * Что модель умеет — из `supported_parameters` провайдера. Нужно, чтобы
   * админка могла предупредить до выбора, а не после первой ошибки: `json`
   * (строгий json_schema) требуется всем задачам, `tools` — проверке цен,
   * `vision` — скриншотам. Модель без нужного флага не «работает хуже», она
   * не работает вовсе.
   */
  json: boolean;
  tools: boolean;
  reasoning: boolean;
  /** Рассуждение не отключается: его токены съедают тот же лимит ответа. */
  reasoningMandatory: boolean;
  temperature: boolean;
  /** Потолок ответа у провайдера, если он его объявил. */
  maxCompletionTokens: number | null;
}

export interface AiSettingsView {
  settings: AiSettings;
  key: AiKeyInfo;
  updatedAt: number | null;
  updatedBy: string;
}

export interface AiModelsView {
  models: AiModel[];
  fetchedAt: number;
  cached: boolean;
}

export interface AiTestResult {
  ok: true;
  model: string;
  ms: number;
  sample: string;
}

export function fetchAiSettings(): Promise<ApiResult<AiSettingsView>> {
  return request('/api/admin/ai/settings');
}

/** apiKey: undefined — не трогать сохранённый ключ; null — удалить его. */
export function saveAiSettings(patch: {
  settings?: Omit<Partial<AiSettings>, 'overrides'> & { overrides?: AiOverridesPatch };
  apiKey?: string | null;
}): Promise<ApiResult<AiSettingsView>> {
  return request('/api/admin/ai/settings', { method: 'PUT', json: patch });
}

export function fetchAiModels(refresh?: boolean): Promise<ApiResult<AiModelsView>> {
  return request(`/api/admin/ai/models${refresh ? '?refresh=1' : ''}`);
}

export function testAiSource(source: AiSource): Promise<ApiResult<AiTestResult>> {
  return request('/api/admin/ai/test', { method: 'POST', json: { source } });
}
